/**
 * Reduce el peso de un logo en el navegador antes de subirlo.
 *
 * El logo de la compañía viaja en los correos, el CAFE y el PDF de cada factura,
 * así que una imagen de 1MB se arrastra en cada documento emitido. Se redimensiona
 * al lado mayor `maxSide` y se re-codifica:
 *   - PNG se mantiene PNG (conserva la transparencia del logo).
 *   - JPEG/WebP salen como JPEG con calidad decreciente hasta quedar bajo `targetBytes`.
 *   - GIF y SVG se devuelven sin tocar (animación / vectorial).
 * Si la imagen ya es chica y no necesita redimensionarse, se devuelve la original.
 */

export const LOGO_MAX_INPUT_BYTES = 5 * 1024 * 1024; // lo que acepta el selector antes de comprimir
export const LOGO_MAX_UPLOAD_BYTES = 1 * 1024 * 1024; // límite del BFF (multer en company.routes.ts)

interface CompressOptions {
  maxSide?: number;
  targetBytes?: number;
}

const COMPRESSIBLE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer la imagen'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function renameWithExtension(name: string, ext: string): string {
  const base = name.replace(/\.[^.]+$/, '') || 'logo';
  return `${base}.${ext}`;
}

export async function compressImage(file: File, options: CompressOptions = {}): Promise<File> {
  const { maxSide = 600, targetBytes = 300 * 1024 } = options;

  if (!COMPRESSIBLE_TYPES.includes(file.type)) {
    return file;
  }

  const img = await loadImage(file);
  const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));

  if (scale === 1 && file.size <= targetBytes) {
    return file;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return file;
  }

  const keepPng = file.type === 'image/png';
  if (!keepPng) {
    // JPEG no tiene canal alfa: fondo blanco en vez de negro para zonas transparentes.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  let blob: Blob | null = null;
  if (keepPng) {
    blob = await canvasToBlob(canvas, 'image/png');
  } else {
    for (const quality of [0.85, 0.75, 0.65, 0.5]) {
      blob = await canvasToBlob(canvas, 'image/jpeg', quality);
      if (blob && blob.size <= targetBytes) break;
    }
  }

  // Si la re-codificación no mejora, se conserva el archivo original.
  if (!blob || blob.size >= file.size) {
    return file;
  }

  const type = keepPng ? 'image/png' : 'image/jpeg';
  const ext = keepPng ? 'png' : 'jpg';
  return new File([blob], renameWithExtension(file.name, ext), { type, lastModified: Date.now() });
}
